import { Big } from "big.js";
import { ONE, ZERO } from "./bigUtil.ts";
import { CostType, Stage } from "./CaseConfiguration.ts";
import type { CostApportionment } from "./CaseModel.ts";
import type {
  Budget,
  CostItem,
  CostPart,
  LockedCosts,
} from "./CostModel.ts";
import randomId from "./randomId.ts";

/*
 * How the pages change a party's costs (SettlementFreezeOnSave.test.ts: R4,
 * R5, R9, R10, R13, R14). What accepted settlements locked stays as it is;
 * the rest of the costs, the open costs, are what the pages change. They
 * write a stage's costs of a type as one item.
 */

/** What of an amount is incurred and what estimated. */
export type Amounts = { incurred: Big; estimated: Big };

/** What a party without accepted settlements has locked: nothing. */
export const NOTHING_LOCKED: LockedCosts = { ended: {}, costs: [] };

/** Costs of a stage and type, or what of them is locked or open: towards each opponent, and generic. */
type Parts = { towards: { [opponentId: string]: Amounts }; generic: Amounts };

const none = (): Amounts => ({ incurred: ZERO, estimated: ZERO });
const totalOf = ({ incurred, estimated }: Amounts): Big => incurred.add(estimated);
const plus = (a: Amounts, b: Amounts | CostPart): Amounts => ({
  incurred: a.incurred.add(b.incurred),
  estimated: a.estimated.add(b.estimated),
});
const minus = (a: Amounts, b: Amounts): Amounts => ({
  incurred: a.incurred.minus(b.incurred),
  estimated: a.estimated.minus(b.estimated),
});

const targetsOf = ({ towards, generic }: Parts): Amounts[] => [
  ...Object.values(towards),
  generic,
];
const sumOf = (parts: Parts): Amounts => targetsOf(parts).reduce(plus, none());

const mapParts = (
  { towards, generic }: Parts,
  change: (amounts: Amounts) => Amounts,
): Parts => ({
  towards: Object.fromEntries(
    Object.entries(towards).map(([id, amounts]) => [id, change(amounts)]),
  ),
  generic: change(generic),
});

const addParts = (a: Parts, b: Parts): Parts => {
  const towards = { ...a.towards };
  for (const [id, amounts] of Object.entries(b.towards))
    towards[id] = plus(towards[id] ?? none(), amounts);
  return { towards, generic: plus(a.generic, b.generic) };
};

const itemsAt = (budget: Budget, stage: Stage, type: CostType): CostItem[] =>
  budget.costs
    .filter((costs) => costs.stage === stage)
    .flatMap(({ flatFees }) => flatFees)
    .filter((item) => item.type === type);

/** A party's costs of a stage and type, summed over its items. */
function partsAt(budget: Budget, stage: Stage, type: CostType): Parts {
  const parts: Parts = { towards: {}, generic: none() };
  for (const { towards, generic } of itemsAt(budget, stage, type)) {
    for (const [id, part] of Object.entries(towards))
      parts.towards[id] = plus(parts.towards[id] ?? none(), part);
    parts.generic = plus(parts.generic, generic);
  }
  return parts;
}

/** What settlements locked of a party's costs of a stage and type. */
function lockedPartsAt(
  locked: LockedCosts,
  stage: Stage,
  type: CostType,
): Parts {
  const parts: Parts = { towards: {}, generic: none() };
  for (const cost of locked.costs) {
    if (cost.stage !== stage || cost.type !== type) continue;
    if (cost.opponentId === undefined) parts.generic = plus(parts.generic, cost);
    else
      parts.towards[cost.opponentId] = plus(
        parts.towards[cost.opponentId] ?? none(),
        cost,
      );
  }
  return parts;
}

/** What of a party's costs of a stage and type is open: all but what settlements locked. */
function openAt(
  budget: Budget,
  locked: LockedCosts,
  stage: Stage,
  type: CostType,
): Parts {
  const fixed = lockedPartsAt(locked, stage, type);
  return mapPartsWith(partsAt(budget, stage, type), fixed, minus);
}

const mapPartsWith = (
  a: Parts,
  b: Parts,
  combine: (a: Amounts, b: Amounts) => Amounts,
): Parts => ({
  towards: Object.fromEntries(
    Object.entries(a.towards).map(([id, amounts]) => [
      id,
      combine(amounts, b.towards[id] ?? none()),
    ]),
  ),
  generic: combine(a.generic, b.generic),
});

/** The cost types of a stage: of its items, and of what settlements locked of it. */
function typesAt(budget: Budget, locked: LockedCosts, stage: Stage): CostType[] {
  const types = new Set<CostType>();
  for (const costs of budget.costs)
    if (costs.stage === stage)
      for (const { type } of costs.flatFees) types.add(type);
  for (const cost of locked.costs) if (cost.stage === stage) types.add(cost.type);
  return [...types];
}

/** The stages a budget has costs at, in their order. */
const stagesOf = (budget: Budget): Stage[] =>
  [...new Set(budget.costs.map(({ stage }) => stage))].sort((a, b) => a - b);

/**
 * What settlements locked of a party's costs, of a stage or all, of a type or
 * all: what of it was incurred stays incurred, the rest is not to be spent.
 */
export function lockedAt(
  locked: LockedCosts,
  stage?: Stage,
  type?: CostType,
): Amounts {
  return locked.costs
    .filter(
      (cost) =>
        (stage === undefined || cost.stage === stage) &&
        (type === undefined || cost.type === type),
    )
    .reduce(plus, none());
}

/** True while some of a party's costs at `stages` are open: its Cost progress slider can move. */
export const hasOpenCosts = (
  budget: Budget,
  locked: LockedCosts,
  stages: Stage[],
): boolean =>
  stages.some((stage) => stageShareOf(budget, locked, stage) !== undefined);

const part = ({ incurred, estimated }: Amounts): CostPart => ({
  incurred: incurred.toNumber(),
  estimated: estimated.toNumber(),
});

/**
 * The budget with a stage's costs of a type as one item of `parts`, in the
 * place of the first item of the type; parts that come to nothing go.
 */
function withPartsAt(
  budget: Budget,
  stage: Stage,
  type: CostType,
  parts: Parts,
): Budget {
  const [first] = itemsAt(budget, stage, type);
  const item: CostItem = {
    id: first?.id ?? randomId(),
    type,
    ...(first?.note !== undefined ? { note: first.note } : {}),
    towards: Object.fromEntries(
      Object.entries(parts.towards)
        .filter(([, amounts]) => !totalOf(amounts).eq(0))
        .map(([id, amounts]) => [id, part(amounts)]),
    ),
    generic: part(parts.generic),
  };
  const empty = totalOf(sumOf(parts)).eq(0);
  let placed = false;
  const costs = budget.costs.map((stageCosts) => {
    if (stageCosts.stage !== stage) return stageCosts;
    const flatFees = stageCosts.flatFees.flatMap((other) => {
      if (other.type !== type) return [other];
      if (placed || empty) return [];
      placed = true;
      return [item];
    });
    return { ...stageCosts, flatFees };
  });
  if (!placed && !empty) {
    const at = costs.findIndex((stageCosts) => stageCosts.stage === stage);
    if (at >= 0)
      costs[at] = { ...costs[at], flatFees: [...costs[at].flatFees, item] };
    else costs.push({ id: randomId(), name: Stage[stage], stage, flatFees: [item] });
  }
  return { ...budget, costs };
}

// ---------------------------------------------------------------------------
// the Cost progress slider (R4)

/** What of a stage's open costs, of all its types, is incurred; nothing where none is open. */
export function stageShareOf(
  budget: Budget,
  locked: LockedCosts,
  stage: Stage,
): Big | undefined {
  let open = none();
  for (const type of typesAt(budget, locked, stage))
    open = plus(open, sumOf(openAt(budget, locked, stage, type)));
  const total = totalOf(open);
  return total.gt(0) ? open.incurred.div(total) : undefined;
}

/**
 * Where the Cost progress slider stands among `stages`: at the last stage
 * whose open costs are partly incurred, at what of them is.
 */
export function sliderPositionOf(
  budget: Budget,
  locked: LockedCosts,
  stages: Stage[],
): { stage: Stage; share: Big } {
  for (const stage of [...stages].reverse()) {
    const share = stageShareOf(budget, locked, stage);
    if (share?.gt(0)) return { stage, share };
  }
  return { stage: stages[0], share: ZERO };
}

/**
 * The budget with the Cost progress slider at `share` of `stage`: the open
 * costs of the stages before it incurred, of those after it estimated, of
 * `stage` that share. Moved within a stage, the slider changes every open
 * part by the same share: moving up, of its estimated amount; moving down, of
 * its incurred amount.
 */
export function incurredUpTo(
  budget: Budget,
  locked: LockedCosts,
  stage: Stage,
  share: Big | number,
): Budget {
  let result = budget;
  for (const at of stagesOf(budget)) {
    const target = at < stage ? ONE : at > stage ? ZERO : new Big(share);
    result = withShareAt(result, budget, locked, at, target);
  }
  return result;
}

/**
 * The budget with `share` of a stage's open costs incurred, and the other
 * stages as they are: the Cost progress slider within the open stage of the
 * timeline, whose closed stages keep what closing them recorded (see
 * docs/InsurerReserves.md, step 2). Each open part changes by the same share,
 * as with incurredUpTo.
 */
export const incurredWithin = (
  budget: Budget,
  locked: LockedCosts,
  stage: Stage,
  share: Big | number,
): Budget => withShareAt(budget, budget, locked, stage, new Big(share));

/** `result` with `target` of the open costs of `budget` at a stage incurred. */
function withShareAt(
  result: Budget,
  budget: Budget,
  locked: LockedCosts,
  at: Stage,
  target: Big,
): Budget {
  const current = stageShareOf(budget, locked, at);
  if (current === undefined || current.eq(target)) return result;
  const up = target.gt(current);
  // up: what stays estimated; down: what stays incurred
  const factor = up
    ? ONE.minus(target).div(ONE.minus(current))
    : target.div(current);
  for (const type of typesAt(budget, locked, at)) {
    const open = openAt(budget, locked, at, type);
    const moved = mapParts(open, ({ incurred, estimated }) => {
      const amount = incurred.add(estimated);
      if (up) {
        const stays = estimated.mul(factor);
        return { incurred: amount.minus(stays), estimated: stays };
      }
      const stays = incurred.mul(factor);
      return { incurred: stays, estimated: amount.minus(stays) };
    });
    result = withPartsAt(
      result,
      at,
      type,
      addParts(lockedPartsAt(locked, at, type), moved),
    );
  }
  return result;
}

// ---------------------------------------------------------------------------
// the Cost editor (R5, R13)

/** What settlements dropped of a stage's costs of a type: the Cost editor's fixed number. */
export const fixedAt = (
  locked: LockedCosts,
  stage: Stage,
  type: CostType,
): Big => totalOf(sumOf(lockedPartsAt(locked, stage, type)));

/**
 * An amount split by a party's apportionment among the opponents still in the
 * case and the generic costs, as their shares relate, `level` of it incurred.
 */
function splitAmong(
  amount: Big,
  level: Big,
  apportionment: CostApportionment | undefined,
  ended: LockedCosts["ended"],
): Parts {
  const shares = Object.entries(apportionment?.perParty ?? {});
  const rest = shares.reduce((left, [, share]) => left.minus(share), ONE);
  const weights = shares.filter(
    ([opponentId, share]) => share > 0 && ended[opponentId] === undefined,
  );
  const generic = rest.gt(0) ? rest : ZERO;
  const total = weights.reduce((sum, [, share]) => sum.add(share), generic);
  const of = (weight: Big): Amounts => {
    const ofIt = total.gt(0) ? amount.mul(weight).div(total) : amount;
    return { incurred: ofIt.mul(level), estimated: ofIt.minus(ofIt.mul(level)) };
  };
  return {
    towards: Object.fromEntries(
      weights.map(([opponentId, share]) => [opponentId, of(new Big(share))]),
    ),
    generic: total.gt(0) ? of(generic) : of(ONE),
  };
}

/**
 * The budget with the open costs of a stage and type at `amount`, as the Cost
 * editor's input sets them; what settlements dropped stays. What is added is
 * split as the stage's open parts of the type relate, at the stage's level,
 * the share of its open costs incurred; what is taken off comes off each open
 * part at its own ratio of incurred and estimated. A stage with no open costs
 * of the type splits what is entered by the party's apportionment among the
 * opponents still in the case and the generic costs, incurred before the
 * slider's stage and estimated after it.
 */
export function withOpenAmount(
  budget: Budget,
  locked: LockedCosts,
  apportionment: CostApportionment | undefined,
  stages: Stage[],
  stage: Stage,
  type: CostType,
  amount: Big | number,
): Budget {
  const open = openAt(budget, locked, stage, type);
  const was = totalOf(sumOf(open));
  const now = new Big(amount);
  let changed: Parts;
  if (was.gt(0) && now.gte(was)) {
    const added = now.minus(was);
    const level = stageShareOf(budget, locked, stage)!;
    changed = mapParts(open, (amounts) => {
      const add = added.mul(totalOf(amounts)).div(was);
      const incurred = add.mul(level);
      return {
        incurred: amounts.incurred.add(incurred),
        estimated: amounts.estimated.add(add.minus(incurred)),
      };
    });
  } else if (was.gt(0)) {
    const factor = now.div(was);
    changed = mapParts(open, ({ incurred, estimated }) => ({
      incurred: incurred.mul(factor),
      estimated: estimated.mul(factor),
    }));
  } else {
    const position = sliderPositionOf(budget, locked, stages);
    const level =
      stageShareOf(budget, locked, stage) ??
      (stage < position.stage
        ? ONE
        : stage > position.stage
          ? ZERO
          : position.share);
    changed = splitAmong(now, level, apportionment, locked.ended);
  }
  return withPartsAt(
    budget,
    stage,
    type,
    addParts(lockedPartsAt(locked, stage, type), changed),
  );
}

// ---------------------------------------------------------------------------
// the Cost apportionments page (R9, R10, R13, R14)

// the key of the generic costs in a split, which no opponent has
const GENERIC = "";

/** A stage's costs towards each opponent and generic, of all its types. */
function splitAt(budget: Budget, stage: Stage): { [target: string]: Big } {
  const split: { [target: string]: Big } = {};
  const add = (target: string, { incurred, estimated }: CostPart) =>
    (split[target] = (split[target] ?? ZERO).add(incurred).add(estimated));
  for (const costs of budget.costs)
    if (costs.stage === stage)
      for (const { towards, generic } of costs.flatFees) {
        for (const [id, amounts] of Object.entries(towards)) add(id, amounts);
        add(GENERIC, generic);
      }
  return split;
}

/** The stages with costs, in their order. */
export const stagesWithCosts = (budget: Budget): Stage[] =>
  stagesOf(budget).filter((stage) => totalAt(budget, stage).gt(0));

/** A stage's costs, of all its types: towards each opponent, generic, and all of them. */
export function splitOfStage(
  budget: Budget,
  stage: Stage,
): { towards: { [opponentId: string]: Big }; generic: Big; total: Big } {
  const { [GENERIC]: generic = ZERO, ...towards } = splitAt(budget, stage);
  return { towards, generic, total: totalAt(budget, stage) };
}

/** What of a stage's costs, of all its types, is towards an opponent. */
const towardsAt = (budget: Budget, stage: Stage, opponentId: string): Big =>
  splitAt(budget, stage)[opponentId] ?? ZERO;

/** All of a stage's costs. */
const totalAt = (budget: Budget, stage: Stage): Big =>
  Object.values(splitAt(budget, stage)).reduce((sum, amount) => sum.add(amount), ZERO);

/**
 * How far a stage's costs towards an opponent can go: down by what of them is
 * open, up into the open generic costs; not at all towards an opponent whose
 * case ended.
 */
export function boundsTowardsAt(
  budget: Budget,
  locked: LockedCosts,
  stage: Stage,
  opponentId: string,
): { min: Big; max: Big } {
  const current = towardsAt(budget, stage, opponentId);
  if (locked.ended[opponentId] !== undefined)
    return { min: current, max: current };
  let ownOpen = ZERO;
  let genericOpen = ZERO;
  for (const type of typesAt(budget, locked, stage)) {
    const open = openAt(budget, locked, stage, type);
    ownOpen = ownOpen.add(totalOf(open.towards[opponentId] ?? none()));
    genericOpen = genericOpen.add(totalOf(open.generic));
  }
  return { min: current.minus(ownOpen), max: current.add(genericOpen) };
}

/**
 * The budget with a stage's costs towards an opponent at `amount`, as a
 * slider or an amount field of the Cost apportionments page sets them. What
 * moves comes out of the open generic costs where the costs towards the
 * opponent grow, out of the opponent's open costs where they shrink, of each
 * type as their open costs relate, and at the ratio of incurred and estimated
 * of the part it leaves: the stage's incurred costs stay as they were.
 */
export function withAmountTowardsAt(
  budget: Budget,
  locked: LockedCosts,
  stage: Stage,
  opponentId: string,
  amount: Big | number,
): Budget {
  const delta = new Big(amount).minus(towardsAt(budget, stage, opponentId));
  if (delta.eq(0)) return budget;
  const grows = delta.gt(0);
  const opens = typesAt(budget, locked, stage).map(
    (type) => [type, openAt(budget, locked, stage, type)] as const,
  );
  const leftBy = (open: Parts): Amounts =>
    grows ? open.generic : (open.towards[opponentId] ?? none());
  const available = opens.reduce(
    (sum, [, open]) => sum.add(totalOf(leftBy(open))),
    ZERO,
  );
  if (available.eq(0)) return budget;
  const share = delta.abs().div(available);
  let result = budget;
  for (const [type, open] of opens) {
    const left = leftBy(open);
    if (totalOf(left).eq(0)) continue;
    const moved = { incurred: left.incurred.mul(share), estimated: left.estimated.mul(share) };
    const towards = open.towards[opponentId] ?? none();
    const changed: Parts = grows
      ? {
          towards: { ...open.towards, [opponentId]: plus(towards, moved) },
          generic: minus(open.generic, moved),
        }
      : {
          towards: { ...open.towards, [opponentId]: minus(towards, moved) },
          generic: plus(open.generic, moved),
        };
    result = withPartsAt(
      result,
      stage,
      type,
      addParts(lockedPartsAt(locked, stage, type), changed),
    );
  }
  return result;
}

/**
 * How far an opponent's share of every stage can go in simple mode: within
 * what every stage has available.
 */
export function shareBoundsOfBudget(
  budget: Budget,
  locked: LockedCosts,
  opponentId: string,
): { min: Big; max: Big } {
  let min = ZERO;
  let max = ONE;
  for (const stage of stagesOf(budget)) {
    const total = totalAt(budget, stage);
    if (total.eq(0)) continue;
    const bounds = boundsTowardsAt(budget, locked, stage, opponentId);
    const low = bounds.min.div(total);
    const high = bounds.max.div(total);
    if (low.gt(min)) min = low;
    if (high.lt(max)) max = high;
  }
  return { min, max };
}

/** The budget with an opponent's share of every stage at `share`, as simple mode sets it. */
export const withShareOfBudget = (
  budget: Budget,
  locked: LockedCosts,
  opponentId: string,
  share: Big | number,
): Budget =>
  stagesOf(budget).reduce(
    (result, stage) =>
      withAmountTowardsAt(
        result,
        locked,
        stage,
        opponentId,
        totalAt(budget, stage).mul(share),
      ),
    budget,
  );

/** True while every stage with costs has exactly the same split: simple mode is offered then. */
export function hasEqualSplits(budget: Budget): boolean {
  const splits = stagesOf(budget)
    .map((stage) => ({ split: splitAt(budget, stage), total: totalAt(budget, stage) }))
    .filter(({ total }) => total.gt(0));
  const targets = new Set(splits.flatMap(({ split }) => Object.keys(split)));
  const [first, ...others] = splits;
  return others.every(({ split, total }) =>
    [...targets].every((target) =>
      (split[target] ?? ZERO)
        .mul(first.total)
        .eq((first.split[target] ?? ZERO).mul(total)),
    ),
  );
}

/** The shares of the whole budget, what settlements dropped included. */
export function sharesOfBudget(budget: Budget): {
  towards: { [opponentId: string]: Big };
  generic: Big;
} {
  const split: { [target: string]: Big } = {};
  for (const stage of stagesOf(budget))
    for (const [target, amount] of Object.entries(splitAt(budget, stage)))
      split[target] = (split[target] ?? ZERO).add(amount);
  const total = Object.values(split).reduce((sum, amount) => sum.add(amount), ZERO);
  const shareOf = (amount: Big) => (total.gt(0) ? amount.div(total) : ZERO);
  const { [GENERIC]: generic = ZERO, ...towards } = split;
  return {
    towards: Object.fromEntries(
      Object.entries(towards).map(([id, amount]) => [id, shareOf(amount)]),
    ),
    generic: shareOf(generic),
  };
}

// ---------------------------------------------------------------------------
// the Cost progress figure (R12)

/** What settlements leave not to be spent of a party's costs: the estimates they locked. */
export const noLongerToBeSpent = (locked: LockedCosts): Big =>
  locked.costs.reduce((sum, { estimated }) => sum.add(estimated), ZERO);

// ---------------------------------------------------------------------------
// closing a stage (docs/InsurerReserves.md, step 2)

/**
 * A stage's open costs by type, as closing it shows them: what is planned of
 * each, incurred and estimated together, and what of it is incurred. A type
 * with nothing open is left out: settlements locked all of it, or there is
 * none.
 */
export function plannedAt(
  budget: Budget,
  locked: LockedCosts,
  stage: Stage,
): { type: CostType; planned: Big; spent: Big }[] {
  return typesAt(budget, locked, stage).flatMap((type) => {
    const open = sumOf(openAt(budget, locked, stage, type));
    const planned = totalOf(open);
    return planned.gt(0) ? [{ type, planned, spent: open.incurred }] : [];
  });
}

/**
 * What of a party's costs is still estimated in the stages before `stage`,
 * by stage and type.
 */
export function openEstimates(
  budget: Budget,
  locked: LockedCosts,
  stage: Stage,
): { stage: Stage; type: CostType; planned: Big; estimated: Big }[] {
  return stagesOf(budget)
    .filter((before) => before < stage)
    .flatMap((before) =>
      plannedAt(budget, locked, before).flatMap(({ type, planned, spent }) =>
        planned.gt(spent)
          ? [{ stage: before, type, planned, estimated: planned.minus(spent) }]
          : [],
      ),
    );
}

/**
 * The budget with what was actually spent on a stage's open costs of a type,
 * as closing the stage enters it. Each open part is spent at the ratio of its
 * planned amount to the type's, the apportionment so far: spent below what is
 * planned, the rest of each part stays estimated; above it, each part grows
 * by its share of the difference, which raises the budget. What settlements
 * locked stays. A type with nothing open has no ratio, and is left as it is.
 */
export function withSpentAt(
  budget: Budget,
  locked: LockedCosts,
  stage: Stage,
  type: CostType,
  spent: Big | number,
): Budget {
  const open = openAt(budget, locked, stage, type);
  const planned = totalOf(sumOf(open));
  if (!planned.gt(0)) return budget;
  const factor = new Big(spent).div(planned);
  const changed = mapParts(open, (amounts) => {
    const total = totalOf(amounts);
    const incurred = total.mul(factor);
    return {
      incurred,
      estimated: incurred.gte(total) ? ZERO : total.minus(incurred),
    };
  });
  return withPartsAt(
    budget,
    stage,
    type,
    addParts(lockedPartsAt(locked, stage, type), changed),
  );
}
